<template>
  <button
    :class="[
      'mobile-menu-button',
      { 'is-visible': !controlLayout.mobileMenuOpen },
    ]"
    :aria-hidden="controlLayout.mobileMenuOpen"
    @click="setMobileMenuOpen(true)"
  >
    <x-icon icon="hamburger-menu" height="20" color="#fff" />
  </button>
  <x-logo
    v-if="mobileTestbedActive && !isExploreEnabled"
    color="light"
    height="30px"
    class="mobile-prototype-logo"
  />
  <div
    :id="appId"
    :data-checkly="appId"
    :class="['side-nav', { 'is-visible': controlLayout.mobileMenuOpen }]"
    data-tool-tip-target="main-nav"
  >
    <button
      class="nav-logo"
      :inert="!isMobile"
      @click="setMobileMenuOpen(false)"
    >
      <x-logo :compact="true" />
    </button>
    <nav
      ref="navContainer"
      class="nav-links"
      data-test="Nav-Links"
      :aria-hidden="!controlLayout.mobileMenuOpen"
      @scroll="handleScroll"
    >
      <div
        ref="mainNavLinks"
        :class="['main-nav-links', { hidden: !controlLayout.nav }]"
      >
        <slot name="nav-links" />

        <mobile-tools-nav-link />
        <!-- Teleport destination when mobileTestbedActive is false -->
      </div>

      <div class="nav-spacer" />
      <!-- Teleport destination when mobileTestbedActive is true  -->
      <teleport v-if="teleportTarget" :to="teleportTarget">
        <button
          :class="[
            'account-settings-btn',
            'nav-link',
            { 'account-settings-btn--mobile': mobileTestbedActive },
          ]"
          :aria-expanded="accordionOpen"
          data-test="Account-Settings"
          @click="toggleAccordion"
        >
          <x-img class="profile-pic avatar-img" :src="avatars" alt="avatar" />
          <div class="text">My Account & Settings</div>
          <x-icon :icon="accountSettingsArrow" class="arrow" />
        </button>
        <animation-wrap :type="ANIMATION_TYPES.FADE">
          <div v-if="accordionOpen" class="accordion-nav">
            <router-link
              class="accordion-link"
              :to="{
                name: accountRoute,
                params: { action: 'overview' },
              }"
              data-test="My-Account"
              @click.prevent="handleLinkClick"
            >
              <x-icon icon="profile" class="accordion-icon" />
              My Account
            </router-link>
            <router-link
              class="accordion-link"
              :aria-current="isSettingsOpen ? 'page' : false"
              :to="{ name: settingsRoute }"
              data-test="Settings"
              @click.prevent="handleLinkClick"
            >
              <x-icon icon="settings" class="accordion-icon" />
              Settings
            </router-link>
            <router-link
              :to="{ name: 'referral' }"
              class="accordion-link"
              @click.prevent="handleLinkClick"
            >
              <x-icon icon="friends" class="accordion-icon" />
              Invite Friends
            </router-link>

            <button
              type="button"
              class="accordion-link print"
              @click="printHandler"
            >
              <x-icon icon="print" class="accordion-icon" />
              Print
            </button>
          </div>
        </animation-wrap>
      </teleport>
    </nav>
  </div>
</template>
<!-- eslint-disable vue/block-lang -->
<script setup>
///// imports /////
import { computed, ref, onMounted, watchEffect, nextTick, watch } from 'vue'
import { useRoute } from 'vue-router'

import { useSideNavFunctions } from '@/composables/nav-links'
import { useOnResize } from '@/composables/element-resize'

import pubsub from '@/lib/pubsub'
import { ANIMATION_TYPES } from '@/lib/enums'
import xIcon from '@/components/shared/x-icon'
import xLogo from '@/components/shared/x-logo'
import xImg from '@/components/shared/x-img'
import animationWrap from '@/components/shared/animation-wrap'
import mobileToolsNavLink from '@/components/mobile-prototyping/mobile-tools-nav-link.vue'

import {
  getCurrentUserProfile,
  getLatestUserProfilePictureUrl,
} from '@/services/profile-service'
import marketingEventService from '@/services/marketing-event-service'

import {
  controlState,
  setMobileMenuOpen,
} from '@/services/control-layout-service'

import { NAV_ROUTES } from '@/components/side-nav/constants'
import defaultAvatar from '@/assets/images/default-avatar.png'
import {
  isMobileTestbedActive,
  isExploreEnabled,
} from '@/lib/prototyping-flags'

///// refs and variables /////
const avatars = ref([defaultAvatar])
const accordionOpen = ref(false)
const isMobile = ref(false)
/** @type {import('vue').Ref<HTMLElement|null>} */
const navContainer = ref(null)

///// refs and variables /////
const route = useRoute()
const { handleLinkClick } = useSideNavFunctions()
const { width } = useOnResize(document.body)

const teleportTarget = ref(null)
const mainNavLinks = ref(null)

///// computed /////
const controlLayout = computed(controlState)

const accountSettingsArrow = computed(() => {
  return accordionOpen.value ? 'chevron-down-circle' : 'chevron-up-circle'
})

const accountRoute = computed(() => {
  return NAV_ROUTES.fish.ACCOUNT
})

const settingsRoute = computed(() => {
  return NAV_ROUTES.fish.SETTINGS
})

const isSettingsOpen = computed(() =>
  route.matched.some((r) => r.name === settingsRoute.value)
)

const appId = computed(() => `nav-fish`)

const mobileTestbedActive = computed(() => isMobileTestbedActive())

///// functions /////
const printHandler = () => {
  window.print()
  marketingEventService.publish('clicked_print_button_web')
}

const getProfilePicture = async () => {
  try {
    const response = await getCurrentUserProfile()
    if (response?.picture_url) {
      const userImage = getLatestUserProfilePictureUrl(response.picture_url)
      avatars.value = [userImage, defaultAvatar]
    }
  } catch (err) {
    //fail silently
  }
}

const updateAvatar = (photo) => {
  const newAvatars = [defaultAvatar]
  if (photo.imageURL.length) {
    newAvatars.unshift(photo.imageURL)
  }
  avatars.value = newAvatars
}

const toggleAccordion = () => {
  accordionOpen.value = !accordionOpen.value
  pubsub.publish('accountSettingsToggled')
  /**
   * Scroll to the bottom to ensure that the content opened
   * is visible to the user on shorter viewports.
   */
  if (navContainer.value && accordionOpen.value) {
    nextTick(() => {
      navContainer.value.scrollTo({
        top: navContainer.value.scrollHeight,
        behavior: 'smooth',
      })
    })
  }
}

///// watchers /////
watchEffect(() => {
  // Make sure mobile menu starts closed on resize
  isMobile.value = width.value < 768
  setMobileMenuOpen(!isMobile.value)
})

///// lifecycle /////
onMounted(async () => {
  await getProfilePicture()
  pubsub.subscribe('updatedProfilePic', updateAvatar)

  teleportTarget.value =
    mobileTestbedActive.value && mainNavLinks.value
      ? '.nav-links'
      : '.main-nav-links'
})

// Update teleport target when `mobileTestbedActive` changes
watch(
  mobileTestbedActive,
  async (newValue) => {
    await nextTick()
    teleportTarget.value =
      newValue && mainNavLinks.value ? '.nav-links' : '.main-nav-links'
  },
  { immediate: true }
)
</script>
<style lang="scss" scoped>
@use '@/styles/system/_variables.scss';
@use '@/styles/system/_colors.scss';
@use '@/styles/system/_type.scss';
@use '@/styles/system/_breakpoints.scss';
@use '@/styles/_layer-map.scss';

.mobile-menu-button {
  all: unset;
  display: none;
  @include breakpoints.mobile() {
    &.is-visible {
      display: inline;
      position: absolute;
      z-index: layer-map.$z-index-level-controls;
      top: 32px;
      left: 15px;
    }
  }
}

.mobile-prototype-logo {
  display: none;
  position: absolute;
  top: 30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: layer-map.$z-index-level-controls;

  @include breakpoints.app-prototyping() {
    display: block;
  }
}

.nav-logo {
  all: unset;
  position: relative;
  z-index: variables.$z-index-level-5;
  padding: 14px 26px;
  background: var(--ys-color-background-secondary);
  border-width: 3px;
  border-bottom-style: solid;
  border-color: var(--ys-color-brand-primary);
  @include breakpoints.mobile {
    padding: 20px 18px;
  }

  :deep(svg) {
    width: 48px;
    height: 48px;

    @include breakpoints.mobile {
      width: 40px;
      height: 40px;
    }
  }
}

:deep(.nav-link) {
  @include type.metadata2;
  position: relative;
  padding: var(--ys-spacing-250) var(--ys-spacing-150);
  color: var(--ys-color-button-text-secondary-active);
  text-align: center;
  width: 100%;

  &:hover,
  &:focus-visible {
    background: var(--ys-color-background-secondary);
  }

  @include breakpoints.mobile {
    padding: var(--ys-spacing-250) var(--ys-spacing-50);
  }

  .nav-icon {
    display: block;
    margin: 0 auto 12px;
    width: 22px;

    svg {
      fill: var(--ys-color-button-text-secondary-active);
    }
  }

  &.router-link-active {
    background: var(--ys-color-background-secondary);
    svg {
      fill: var(--ys-color-brand-primary);
    }
  }
}
.side-nav {
  position: relative;
  width: 100px;
  display: flex;
  flex-direction: column;
  background: var(--ys-color-background-primary);
  z-index: layer-map.$z-index-level-side-nav;

  @include breakpoints.mobile {
    height: 100%;
    width: 75px;
    transform: translateX(-75px);
    position: absolute;
    transition: transform 250ms ease;

    &.is-visible {
      transform: translateX(0);
    }
  }

  .nav-links {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: auto;

    .nav-spacer {
      flex-grow: 1;
    }
  }

  .main-nav-links {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  @include breakpoints.app-prototyping {
    .main-nav-links {
      position: fixed;
      bottom: 0;
      left: 0;
      width: 100vw;
      height: var(--mobile-navbar-height);
      display: flex;
      flex-direction: row;
      justify-content: space-around;
      overflow-y: hidden;
      color: var(--ys-color-text-reverse);
      transform: translateX(75px);
      background: var(--ys-color-surface-reverse);

      &.hidden {
        display: none;
      }

      .nav-logo {
        display: none;
      }

      :deep(.nav-icon) {
        path {
          fill: var(--ys-color-text-reverse);
        }

        &.bookmark path {
          stroke: var(--ys-color-text-reverse);
        }
      }

      :deep(.nav-icon) {
        line-height: 0;
        height: 20px;
        margin-bottom: 4px;
      }

      :deep(.nav-link) {
        flex-grow: 1;
        width: 112px;
        color: white;
        padding: var(--ys-spacing-150) var(--ys-spacing-50);

        // Hiding extra base nav items
        &[data-test='My-Garage'],
        &[data-test='Elite-Benefits'],
        &[data-test='My-Stats'],
        &:hover,
        &.router-link-active {
          background-color: var(--ys-color-button-text-secondary-disabled);
        }
      }

      // Hiding Hunt extra base nav items
      :deep(.elite-extras) {
        display: none;
      }
    }

    .nav-spacer {
      display: none;
    }
  }
}

.account-settings-btn {
  cursor: pointer;
  border-radius: 0;
  transition: none;
  width: 100%;
  text-wrap: wrap;
  background: none;
  // Push the button towards the bottom in a flex
  margin-top: auto;

  &.account-settings-btn--mobile {
    margin-top: initial;
  }

  .profile-pic {
    width: 30px;
    height: 30px;
    border-radius: 50%;
  }

  .text {
    padding: 8px 0;
  }

  &:hover,
  &:focus-visible {
    background: var(--ys-color-background-secondary);
  }
}

.accordion-nav {
  .accordion-link {
    @include type.metadata3;
    padding: 20px 5px 20px 10px;
    background: colors.$Grey20;
    color: colors.$Grey100;
    display: flex;
    cursor: pointer;
    border-bottom: solid 1px colors.$Grey30;

    &:hover,
    &:focus-visible {
      background-color: colors.$Grey30;
    }

    .accordion-icon {
      margin-right: 4px;
      font-size: 10px;
      display: flex;
      @include breakpoints.mobile() {
        display: none;
      }
    }

    &.print {
      width: 100%;
      border-radius: 0;
    }
    &.router-link-active {
      background: colors.$Grey30;
      :deep(svg) {
        fill: var(--ys-color-brand-primary);
      }
    }
  }
}
</style>
